import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form, Input, message, Modal, Select } from 'antd';
import styled from 'styled-components';
import { AxiosResponse } from 'axios';
import accountApis from '../../../../../../services/accountApis';
import CurrencyInputPrecision from '../../../../../account/Promotion/InputPercentDollar/CurrencyInputPrecision';
import useGetAllStore from '../../../../services/hooks/useGetAllStore';

interface Props {
  onSuccess?: () => void;
  isEdit?: boolean;
}

interface IFormValues {
  shopId: string;
  amount: number;
  note?: string;
  id: string;
}

export interface TopUpAmountReportModalRef {
  open: (values?: IFormValues) => void;
  close: () => void;
}

const TopUpAmountReportModal = forwardRef<TopUpAmountReportModalRef, Props>(({ onSuccess, isEdit }, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: listSalon, refetch: refetchListSalon } = useGetAllStore(true);


  const onOpen = (values?: IFormValues) => {
    setOpen(true);

    refetchListSalon();

    if (values) {
      form.setFieldsValue({
        shopId: values.shopId,
        amount: values.amount,
        note: values.note,
        id: values.id,
      });
    }
  };

  const onClose = () => {
    setOpen(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      let response: AxiosResponse<{ data: boolean }>;
      if (isEdit) {
        response = await accountApis.updateTopUpAmount({
          id: values.id,
          amount: values.amount,
          note: values.note,
        });
      } else {
        response = await accountApis.createTopUpAmount(values.shopId, {
          amount: values.amount,
          note: values.note,
        });
      }
      if (response.data.data) {
        message.success(isEdit ? 'Top up amount updated successfully' : 'Top up amount created successfully');

        onClose();
        onSuccess?.();
      }
    } catch (error) {
      message.error(isEdit ? 'Failed to update top up amount' : 'Failed to create top up amount');
    } finally {
      setLoading(false);
    }
  };

  const onDone = () => {
    form.submit();
  };

  return (
    <Modal open={open} title={null} footer={null} width={511} centered destroyOnClose={false} closable={false}>
      <ContainerStyled>
        <TitleStyled>Top Up Amount</TitleStyled>
        <Form form={form} onFinish={onFinish} layout='vertical'>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>
          <Form.Item name='shopId' label='Store' rules={[{ required: true, message: 'Please select store' }]}>
            <Select
              size='large'
              showSearch
              optionFilterProp='label'
              className='filter-select'
              style={{ flex: 1 }}
              placeholder='Select store'
              disabled={isEdit}
            >
              {listSalon?.map((item: any) => (
                <Select.Option key={item.shopId} value={item.shopId} label={`${item.shopName} - ${item.zipCode}`}>
                  {item.shopName} - {item.zipCode}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name='amount' label='Amount ($)' rules={[{ required: true, message: 'Please enter amount' }]}>
            <CurrencyInputPrecision decimalLength={4} size='small' />
          </Form.Item>
          <Form.Item name='note' label='Note'>
            <Input.TextArea rows={4} placeholder='Please enter...' />
          </Form.Item>
        </Form>
        <ButtonsStyled>
          <button type='button' className='btn-cancel' onClick={onClose}>
            Close
          </button>
          <button type='button' className='btn-submit' onClick={onDone} disabled={loading}>
            Done
          </button>
        </ButtonsStyled>
      </ContainerStyled>
    </Modal>
  );
});

export default TopUpAmountReportModal;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .ant-form-item {
    margin-bottom: 8px;
  }

  .filter-select {
    height: 48px;
  }
`;

const TitleStyled = styled.div`
  align-self: stretch;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ButtonsStyled = styled.div`
  display: flex;
  gap: 16px;

  button {
    cursor: pointer;
    display: flex;
    height: 48px;
    padding: 16px 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;

    text-align: center;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    &.btn-cancel {
      background: #e3e9ed;
      color: #566a7f;
    }
    &.btn-submit {
      color: #fff;
      background: #ff8890;
    }
    &:hover {
      opacity: 0.8;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
